import { useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'src/core/common/hooks/useTranslation';
import {
  type MappingUpdate,
  useUpdateAnalyticalFieldMapping,
} from 'src/core/modules/accounting-integration/apis/useUpdateAnalyticalFieldMapping';
import {
  type AnalyticalFieldMappingV2,
  type AnalyticalFieldMappingFieldType,
  type InternalValue,
  type AnalyticalFieldValueMappingV2,
} from 'src/core/modules/bookkeep';
import { useAnalyticalFieldMappingQueryV2 } from 'src/core/modules/bookkeep/settings/integrations/hooks/useAnalyticalFieldMappingsQuery';

import { useAccountingSoftwareName } from '../apis';
import { useAnalyticalFieldConfiguration } from './useAnalyticalFieldConfiguration';
import { useHasMapSync } from './useHasMapSync';
import { type AnalyticalFieldExportSettings } from '../apis/useAnalyticalFieldExportSettings';
import { useDeleteSyncSettings } from '../apis/useDeleteSyncSettings';
import {
  type SyncSetting,
  useGetSyncSettings,
} from '../apis/useGetSyncSettings';
import {
  type SyncMode,
  useUpdateSyncSettings,
} from '../apis/useSetSyncSettings';

type Props<TField, TValue> = {
  shouldValidateValues?: boolean;
  field?: TField;
  values?: TValue[];
  fieldType?: AnalyticalFieldMappingFieldType;
  onUpdated?: () => void;
  onDeleteValue?: (value: TValue) => void;
};

type ErrorType = {
  valuesNotMapped: string[];
};

export type MappingOnboardingState = 'setup' | 'maintenance';

export type AnalyticalFieldMappingData<TValue extends InternalValue> = {
  analyticalFieldMappings: AnalyticalFieldMappingV2[];
  fieldMapping: AnalyticalFieldMappingV2 | undefined;
  isMapSyncEnabled: boolean;
  isFieldMappingsLoading: boolean;
  isSyncSettingsLoading: boolean;
  isIntegrationNameLoading: boolean;
  isExportConfigLoading: boolean;
  isExportConfigEnabled: boolean;
  isMappingRequired: boolean;
  hasSyncSettings: boolean;
  showWarningCallout: boolean;
  syncMode: SyncMode | undefined;
  allSyncSettings: SyncSetting[];
  externalValues: Map<string, string | undefined>;
  sortedValues: TValue[];
  exportConfig: AnalyticalFieldExportSettings[];
  fieldExportSetting: AnalyticalFieldExportSettings | undefined;
  isUpdating: boolean;
  mappingState: MappingOnboardingState | undefined;
  setFieldMapping: React.Dispatch<
    React.SetStateAction<AnalyticalFieldMappingV2 | undefined>
  >;
  reset: () => void;
  onSaveChanges: (arguments_: SaveChanges) => void;
  isDeleteSyncSettingsPending: boolean;
  fieldMappingValues: AnalyticalFieldValueMappingV2[];
  setFieldMappingValues: React.Dispatch<
    React.SetStateAction<AnalyticalFieldValueMappingV2[]>
  >;
  isValid: boolean;
  errors: ErrorType;
  setErrors: React.Dispatch<React.SetStateAction<ErrorType>>;
  handleValueValidation: () => void;
  handleValueMapping: ({
    value,
    internalId,
  }: {
    value?: AnalyticalFieldValueMappingV2;
    internalId: string;
  }) => void;
  onDeleteValue?: (value: TValue) => void;
};

type RemovePreviousMapping = AnalyticalFieldMappingV2 & {
  shouldRemoveSyncSetting?: boolean;
};

export type SaveChanges = {
  mapping?: MappingUpdate;
  previousMapping?: RemovePreviousMapping | undefined;
  syncMode?: SyncMode | undefined;
  exportSettings?: AnalyticalFieldExportSettings | undefined;
};

export type SaveAllChanges =
  AnalyticalFieldMappingData<InternalValue>['onSaveChanges'];

export const useAnalyticalFieldMapping = <
  TField extends InternalValue,
  TValue extends InternalValue,
>(
  props?: Props<TField, TValue>,
): AnalyticalFieldMappingData<TValue> => {
  const isMapSyncEnabled = useHasMapSync();
  const { activeLanguage } = useTranslation('global');
  const { isLoading: isIntegrationNameLoading } = useAccountingSoftwareName();

  const [fieldMapping, setFieldMapping] = useState<AnalyticalFieldMappingV2>();
  const [isMappingRequired, setIsMappingRequired] = useState<boolean>(true);
  const [mappingState, setMappingState] = useState<MappingOnboardingState>();
  const [fieldExportSetting, setFieldExportSetting] =
    useState<AnalyticalFieldExportSettings>();

  const { analyticalFieldMappings, isLoading: isFieldMappingsLoading } =
    useAnalyticalFieldMappingQueryV2(isMapSyncEnabled);

  const { syncSettings, isLoading: isSyncSettingsLoading } =
    useGetSyncSettings();

  const { updateSyncSettings, isPending: isSyncSettingsUpdating } =
    useUpdateSyncSettings();

  const { deleteSyncSettings, isPending: isDeleteSyncSettingsPending } =
    useDeleteSyncSettings();

  const syncSetting = useMemo(
    () => syncSettings?.find((setting) => setting.fieldId === props?.field?.id),
    [syncSettings, fieldMapping],
  );

  const hasSyncSettings = !!syncSetting && !!syncSetting.syncMode;
  const syncMode = hasSyncSettings ? syncSetting.syncMode : undefined;

  const [fieldMappingValues, setFieldMappingValues] = useState<
    AnalyticalFieldValueMappingV2[]
  >([]);

  const [errors, setErrors] = useState<ErrorType>({
    valuesNotMapped: [],
  });

  const {
    isLoading: isExportConfigLoading,
    exportConfig,
    isExportConfigEnabled,
    updateCompanyExportSettings,
    isPending: isExportConfigUpdating,
  } = useAnalyticalFieldConfiguration({
    fieldExternalId: fieldMapping?.externalId,
  });

  useEffect(() => {
    setFieldExportSetting(
      exportConfig.find(
        ({ externalId }) => externalId === fieldMapping?.externalId,
      ) ?? {
        externalId: '',
        sendOnHeader: false,
        sendOnLine: true,
      },
    );
  }, [exportConfig, fieldMapping]);

  useEffect(() => {
    const map = analyticalFieldMappings?.find(
      ({ internalId }) => internalId === props?.field?.id,
    );

    if (map) {
      setFieldMapping(map);
      setFieldMappingValues(map.values);
    }
  }, [analyticalFieldMappings, props?.field]);

  useEffect(() => {
    if (!isSyncSettingsLoading) {
      setMappingState(!syncMode ? 'setup' : 'maintenance');
    }
  }, [isSyncSettingsLoading, syncMode]);

  const { updateAnalyticalFieldMapping, isPending: isFieldMappingUpdating } =
    useUpdateAnalyticalFieldMapping(props?.onUpdated);

  const externalValues = useMemo(() => {
    const internalValues = new Set((props?.values ?? []).map(({ id }) => id));
    const fieldValues = new Map(
      fieldMapping?.values
        .filter(({ internalId }) => internalId !== null)
        .map(({ internalId, externalName }) => [internalId, externalName]),
    );

    return new Map<string, string | undefined>(
      [...internalValues].map((id) => [id, fieldValues.get(id)]),
    );
  }, [fieldMapping, props?.values]);

  useEffect(() => {
    if (syncMode === 'none') {
      setIsMappingRequired(false);
    } else {
      setIsMappingRequired(
        !fieldMapping?.internalId ||
          fieldMapping?.values.some(({ internalId }) => !internalId),
      );
    }
  }, [fieldMapping, syncMode]);

  const sortedValues = useMemo(
    () =>
      (props?.values ?? []).sort((cc1, cc2) => {
        const hasExternal1 = externalValues.get(cc1.id) != null;
        const hasExternal2 = externalValues.get(cc2.id) != null;
        if (hasExternal1 !== hasExternal2) {
          return hasExternal1 ? 1 : -1;
        }

        return cc1.name.localeCompare(cc2.name, activeLanguage, {
          sensitivity: 'base',
        });
      }),
    [props?.values, externalValues],
  );

  const showWarningCallout =
    isMapSyncEnabled &&
    !isSyncSettingsLoading &&
    !!fieldMapping &&
    !hasSyncSettings;

  const reset = () => {
    setFieldMapping(undefined);
  };

  const handleValueValidation = () => {
    if (!props?.shouldValidateValues) {
      return true;
    }

    const invalidIds: string[] = [];

    for (const { id } of props.values ?? []) {
      if (!fieldMappingValues.some((value) => value.internalId === id)) {
        invalidIds.push(id);
      }
    }

    if (invalidIds.length) {
      setErrors((previous) => ({ ...previous, valuesNotMapped: invalidIds }));
      return false;
    }

    setErrors((previous) => ({ ...previous, valuesNotMapped: [] }));
    return true;
  };

  const handleValueMapping = ({
    value,
    internalId,
  }: {
    value?: AnalyticalFieldValueMappingV2;
    internalId: string;
  }) => {
    if (!fieldMapping) {
      return;
    }

    if (!value) {
      const valueIndex = fieldMapping?.values.findIndex(
        (mapping) => mapping.internalId === internalId,
      );

      if (valueIndex > -1) {
        const updatedValues = [...fieldMapping.values];
        updatedValues[valueIndex] = {
          ...updatedValues[valueIndex],
          internalId: null,
        };
        onSaveChanges({
          mapping: {
            fieldId: fieldMapping.id,
            fieldType: fieldMapping.fieldType,
            internalId: fieldMapping.internalId,
            values: updatedValues,
          },
        });
      }
      return;
    }

    const valueIndex = fieldMapping?.values.findIndex(
      (mapping) => mapping.id === value.id,
    );

    if (valueIndex > -1) {
      const updatedValues = [...fieldMapping.values];
      updatedValues[valueIndex] = {
        ...updatedValues[valueIndex],
        internalId,
      };
      onSaveChanges({
        mapping: {
          fieldId: fieldMapping.id,
          fieldType: fieldMapping.fieldType,
          internalId: fieldMapping.internalId,
          values: updatedValues,
        },
      });
    }
  };

  const onSaveChanges = ({
    mapping,
    previousMapping,
    syncMode: updatedSyncMode,
    exportSettings,
  }: SaveChanges) => {
    const canProceed = handleValueValidation();

    if (!canProceed) {
      return;
    }

    if (previousMapping) {
      updateAnalyticalFieldMapping({
        fieldId: previousMapping.id,
        fieldType: null,
        internalId: null,
        values: previousMapping.values.map((value) => ({
          ...value,
          internalId: null,
        })),
      });

      if (
        previousMapping.shouldRemoveSyncSetting &&
        previousMapping.internalId
      ) {
        deleteSyncSettings(previousMapping.internalId);
      }
    }

    if (mapping) {
      updateAnalyticalFieldMapping(mapping, {
        onSuccess: (field) => {
          if (updatedSyncMode) {
            const syncFieldId = props?.field?.id ?? field.internalId;
            if (syncFieldId) {
              updateSyncSettings({
                fieldId: syncFieldId,
                syncMode: updatedSyncMode,
              });
            }
          }

          if (exportSettings) {
            updateCompanyExportSettings([
              {
                ...exportSettings,
                externalId: field.externalId,
              },
            ]);
          }
        },
      });
    }

    if (!mapping && updatedSyncMode === 'none' && props?.field) {
      updateSyncSettings({
        fieldId: props?.field.id,
        syncMode: 'none',
      });
    }
  };

  return {
    analyticalFieldMappings,
    fieldMapping,
    isMapSyncEnabled,
    isFieldMappingsLoading,
    isSyncSettingsLoading,
    isIntegrationNameLoading,
    isExportConfigLoading,
    isMappingRequired,
    isExportConfigEnabled,
    hasSyncSettings,
    showWarningCallout,
    syncMode,
    allSyncSettings: syncSettings ?? [],
    fieldExportSetting,
    externalValues,
    sortedValues,
    exportConfig,
    mappingState,
    isUpdating:
      isFieldMappingUpdating ||
      isExportConfigUpdating ||
      isSyncSettingsUpdating,
    isDeleteSyncSettingsPending,
    fieldMappingValues,
    setFieldMappingValues,
    setFieldMapping,
    onSaveChanges,
    reset,
    setErrors,
    errors,
    isValid: props?.shouldValidateValues
      ? errors.valuesNotMapped.length === 0
      : true,
    handleValueValidation,
    handleValueMapping,
    onDeleteValue: props?.onDeleteValue,
  };
};
